import React, { type ComponentProps, type FC } from 'react';

import classNames from 'classnames';
import qs from 'qs';

import { formatLocalDate } from '@iconik-io/core/src/date/format';
import type {
  FilesFileSchemaWithId,
  FilesFileSetSchemaWithIdNullable,
  FilesFormatSchemaWithId,
  FilesFormatSchemaWithIdNullable,
} from '@iconik-io/iconik-api/src/services/files';
import { selectFilesByFileSetId } from '@iconik-io/shared-lib/src/redux/entities/selectors/files';
import { getFormatLabel } from '@iconik-io/shared-lib/src/utils/format';
import { Icon, type IconName } from '@iconik-io/uad';
import { UPLOADER_STATUS_FAILED } from '@iconik-io/uploader/src/state';

import CopyButton from '../../components/copy_button/copy_button';
import DebugDumpEntity from '../../components/debug_dump_entity/debug_dump_entity';
import Ellipsis from '../../components/ellipsis/ellipsis';
import { ExternalLink as ExternalLinkComponent } from '../../components/external_link/external_link';
import { Link } from '../../components/link/link';
import { selectFileSetsByFormatId } from '../../ducks/file_sets/selectors';
import { downloadFormatFile, openOriginalFile } from '../../ducks/files/thunks';
import type { FilesAssetSubtitle } from '../../ducks/files/types';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import type { FCC } from '../../types/common';
import IconButton from '../../ui/icon_button/icon_button';
import Label, { VARIANTS } from '../../ui/label/label';
import ProgressPie from '../../ui/progress_pie/progress_pie';
import { filesize } from '../../utils/filesize';
import { getFormatIconType } from '../../utils/media_icon_type';
import type { Uploading } from './types';
import {
  canDownloadFileSet,
  getFileSetsIsgFileId,
  getPathDisplay,
  getRowStatus,
  getStorageIcon,
  isFileStatusConflict,
} from './utils';

import mixins from '../../styles/core/mixins.module.css';
import styles from './asset_files.module.css';

// Explicitly used for displaying a readonly path for the ellipsis component.
type EllipsisPathTextProps = {
  className: string;
  to: ComponentProps<typeof Link>['to'];
};

const EllipsisPathText: FCC<EllipsisPathTextProps> = ({
  children,
  className,
}) => <div className={className}>{children}</div>;

type FileSetProps = {
  fileSet: FilesFileSetSchemaWithIdNullable;
  format: FilesFormatSchemaWithIdNullable;
  onClick?: (fileSet: FilesFileSetSchemaWithIdNullable) => void;
};

export const FileSet: FC<FileSetProps> = ({ fileSet, format, onClick }) => {
  const files = useAppSelector((state) =>
    selectFilesByFileSetId(state, fileSet.id),
  );

  const fileSetFileCount = fileSet.file_count ?? 0;
  const fileCount = Math.max(fileSetFileCount, files.length);

  const name = fileSet.name;
  const status = getRowStatus(format, files, fileSet);
  const filesLabelClickable = Boolean(onClick);
  // While file entities are still loading, show a blank placeholder instead of
  // "0 B", keeping the row height stable.
  const filesLabel =
    fileCount === 1
      ? files[0] && filesize(files[0].size ?? 0)
      : `${fileCount} files`;

  return (
    <div className={styles.fileset}>
      <Ellipsis
        tooltipText={name}
        componentProps={{ className: styles.ellipsis_name }}
      >
        {name}
      </Ellipsis>
      <div className={styles.td_columns_wrap}>
        {filesLabel && filesLabelClickable ? (
          <button
            className={classNames(styles.file_detail, mixins.reset_button)}
            onClick={() => onClick?.(fileSet)}
          >
            {filesLabel}
          </button>
        ) : (
          <span className={styles.file_detail}>{filesLabel ?? '\u00A0'}</span>
        )}

        {isFileStatusConflict(format, files, fileSet) && filesLabelClickable ? (
          <Icon name="message_warning" size="xs" className={styles.conflict} />
        ) : undefined}

        {status ? (
          <Label className={styles.status_label} variant={status.variant} wrap>
            {status.value}
          </Label>
        ) : undefined}
      </div>
    </div>
  );
};

type FileSetsProps = {
  format: FilesFormatSchemaWithIdNullable;
  onClick?: (fileSet: FilesFileSetSchemaWithIdNullable) => void;
};

export const FileSets: FC<FileSetsProps> = ({ format, onClick }) => {
  const fileSets = useAppSelector((state) =>
    selectFileSetsByFormatId(state, format.id),
  );
  return (
    <div className={styles.td_row}>
      {fileSets.map((fileSet) => (
        <React.Fragment key={fileSet.id}>
          <DebugDumpEntity entityKey="file_sets" id={fileSet.id} />
          <FileSet
            key={fileSet.id}
            fileSet={fileSet}
            format={format}
            onClick={onClick}
          />
        </React.Fragment>
      ))}
    </div>
  );
};

type StorageItemProps = {
  className?: string;
  iconName: IconName;
  path?: string;
  storageId?: string;
  readOnly?: boolean;
};

export const StorageItem: FC<StorageItemProps> = ({
  iconName,
  storageId,
  path,
  readOnly,
}) => {
  const pathDisplay = getPathDisplay(path);
  const storage = useAppSelector((state) =>
    storageId ? state.entities.storages[storageId] : undefined,
  );

  return (
    <div className={styles.storage}>
      {storage ? (
        <>
          <Icon name={iconName} className={styles.storage_icon} />
          <Ellipsis
            component={readOnly ? EllipsisPathText : Link}
            tooltipText={`${storage.name}${pathDisplay}`}
            componentProps={{
              className: styles.storage_link,
              to: `/admin/storages/${storageId}/browser?${qs.stringify({
                path,
              })}`,
            }}
          >
            <span className={styles.highlight}>{storage.name}</span>
            {pathDisplay}
          </Ellipsis>
          {pathDisplay && (
            <CopyButton
              className={styles.copy_button}
              placement="top"
              text="Copy path"
              copy={pathDisplay}
              variant={VARIANTS.WHITE}
            />
          )}
        </>
      ) : undefined}
    </div>
  );
};

type FileSetStorageProps = {
  fileSet: FilesFileSetSchemaWithIdNullable;
  readOnly?: boolean;
};

export const FileSetStorage: FC<FileSetStorageProps> = ({
  fileSet,
  readOnly,
}) => {
  const storages = useAppSelector((state) => state.entities.storages);
  const storage = fileSet.storage_id ? storages[fileSet.storage_id] : undefined;
  return (
    <>
      {storage ? (
        <DebugDumpEntity entityKey="storages" id={storage.id} />
      ) : null}
      <StorageItem
        className={styles.storage}
        storageId={fileSet.storage_id ?? undefined}
        path={fileSet.base_dir}
        iconName={getStorageIcon(storage)}
        readOnly={readOnly}
      />
    </>
  );
};

type StorageProps = {
  format: FilesFormatSchemaWithIdNullable;
  readOnly?: boolean;
};

export const Storage: FC<StorageProps> = ({ format, readOnly }) => {
  const fileSets = useAppSelector((state) =>
    selectFileSetsByFormatId(state, format.id),
  );

  if (!fileSets.length) {
    return null;
  }

  return (
    <div className={styles.td_row}>
      {fileSets.map((fileSet) => (
        <FileSetStorage
          key={fileSet.id}
          fileSet={fileSet}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
};

type DownloadOpenFileSetProps = {
  assetId: string;
  format: FilesFormatSchemaWithId;
  fileSet: FilesFileSetSchemaWithIdNullable;
  canDownload: boolean;
};

export const DownloadOpenFileSet: FC<DownloadOpenFileSetProps> = (props) => {
  const { assetId, format, fileSet, canDownload } = props;

  const dispatch = useAppDispatch();
  const storages = useAppSelector((state) => state.entities.storages);
  const files = useAppSelector((state) =>
    selectFilesByFileSetId(state, fileSet.id),
  );

  if (canDownloadFileSet(fileSet, canDownload, files)) {
    return (
      <IconButton
        variant="white"
        iconName="load_down"
        onClick={() =>
          dispatch(
            downloadFormatFile(assetId, format.id, fileSet.id, format.name),
          )
        }
        className={styles.icon_button}
        aria-label={`Download ${format.name.toLowerCase()}`}
      />
    );
  }
  if (getFileSetsIsgFileId(format, fileSet, files, storages)) {
    return (
      <IconButton
        variant="white"
        iconName="file_export"
        onClick={() =>
          dispatch(
            openOriginalFile(
              assetId,
              format.id,
              getFileSetsIsgFileId(format, fileSet, files, storages),
            ),
          )
        }
        className={styles.icon_button}
      />
    );
  }
  return <div className={styles.button_space} />;
};

type DownloadOpenFileSetsProps = {
  assetId: string;
  format: FilesFormatSchemaWithId;
  files: FilesFileSchemaWithId[];
  canDownload: boolean;
};

export const DownloadOpenFileSets: FC<DownloadOpenFileSetsProps> = (props) => {
  const { assetId, format, canDownload } = props;

  const fileSets = useAppSelector((state) =>
    selectFileSetsByFormatId(state, format.id),
  );

  return (
    <div className={styles.cell_buttons}>
      {fileSets.map((fileSet) => (
        <DownloadOpenFileSet
          key={fileSet.id}
          assetId={assetId}
          format={format}
          fileSet={fileSet}
          canDownload={canDownload}
        />
      ))}
    </div>
  );
};

type TitleProps = {
  iconName?: IconName;
  head?: string;
  deck?: string;
};

export const Title: FC<TitleProps> = ({ iconName, head, deck }) => {
  return (
    <div className={styles.td_title}>
      {iconName ? (
        <Icon
          name={iconName || 'file'}
          className={styles.title_icon}
          size="m"
        />
      ) : undefined}
      <div className={styles.td_row}>
        {head && <span className={styles.ellipsis_name}>{head}</span>}
        {deck}
      </div>
    </div>
  );
};

type ProgressProps = {
  value: number;
  name?: string;
  error?: boolean;
};

export const Progress: FC<ProgressProps> = ({ value, error }) => (
  <ProgressPie value={value} variant={error ? 'danger' : 'success'} />
);

type FormatNameProps = {
  format: FilesFormatSchemaWithId;
  progress: Uploading;
  mediaType?: string;
};

export const FormatName: FC<FormatNameProps> = ({
  format,
  mediaType,
  progress,
}) => {
  const deck = getFormatLabel(format, mediaType);

  const fileSets = useAppSelector((state) =>
    selectFileSetsByFormatId(state, format.id),
  );

  if (progress !== undefined && !fileSets?.length) {
    return (
      <Progress
        value={progress.progress}
        name={progress.name}
        error={progress.status === UPLOADER_STATUS_FAILED}
      />
    );
  }
  return (
    <>
      <DebugDumpEntity entityKey="formats" id={format.id} />
      <Title
        iconName={getFormatIconType(format)}
        head={format.name}
        deck={deck}
      />
    </>
  );
};

type SubtitleNameProps = {
  format: FilesFormatSchemaWithId;
  subtitle?: FilesAssetSubtitle;
  progress?: Uploading;
};

export const SubtitleName: FC<SubtitleNameProps> = ({
  format,
  subtitle,
  progress,
}) => {
  let extension: string | undefined = '';

  const fileSets = useAppSelector((state) =>
    selectFileSetsByFormatId(state, format.id),
  );
  if (fileSets?.length) {
    if (fileSets[0]) {
      extension =
        format.metadata?.at(0)?.format ??
        fileSets[0].name.split('.').pop()?.toUpperCase();
    }
  }

  if (progress !== undefined && !fileSets?.length) {
    return (
      <Progress
        value={progress.progress}
        name={progress.name}
        error={progress.status === UPLOADER_STATUS_FAILED}
      />
    );
  }
  return (
    <Title
      iconName="subtitles"
      head={extension}
      deck={(subtitle?.language ?? '').toUpperCase() || 'waiting...'}
    />
  );
};

type FileSetLastUpdateProps = {
  fileSet: FilesFileSetSchemaWithIdNullable;
};

export const FileSetLastUpdate: FC<FileSetLastUpdateProps> = ({ fileSet }) => {
  const dateFormat = useAppSelector((state) => state.appReducer.dateTimeFormat);
  const files = useAppSelector((state) =>
    selectFilesByFileSetId(state, fileSet.id),
  );
  // Math.max will provide with the most recent date
  const value = Math.max(
    ...files.map((file) =>
      file.date_modified ? new Date(file.date_modified).getTime() : 0,
    ),
  );
  return <>{formatLocalDate(new Date(value), dateFormat)}</>;
};

type FileFormatLinkMetadata = {
  action?: string;
  displayName?: string;
  iconName: IconName;
  uri: string;
};

export function FileFormatLink(props: FileFormatLinkMetadata) {
  return (
    <div className={styles.cell_buttons}>
      <ExternalLinkComponent
        href={props.uri}
        className={styles.file_format_link}
      >
        <Icon
          name={props.iconName}
          className={styles.icon_button_less_padding}
        />
      </ExternalLinkComponent>
    </div>
  );
}
